import { Box, FormControl, FormLabel, Option } from "@mui/joy";
import DarkInput from "components/common/DarkInput";
import DarkSelect from "components/common/DarkSelect";
import IconButtonCTA from "components/common/IconButtonCTA";
import { darkBorder, hrAccentColor, textTokens } from "styles/globalStyles";
import { ParamsTemplateSection } from "./ParamsTemplateSection";
import { SDelete, SSave } from "styles/StyledMUI";
import type { ParamsTemplateEntry } from "./paramsTemplateUtils";

export type TemplateFormBaseProps = {
  name: string;
  description: string;
  shouldBeUsedByDefault: boolean;
  onNameChange: (v: string) => void;
  onDescriptionChange: (v: string) => void;
  onShouldBeUsedByDefaultChange: (v: boolean) => void;
  templateEntries: ParamsTemplateEntry[];
  onTemplateEntriesAdd: () => void;
  onTemplateEntriesUpdate: (index: number, entry: ParamsTemplateEntry) => void;
  onTemplateEntriesRemove: (index: number) => void;
  canEdit: boolean;
  saving: boolean;
  onSave: () => void;
  onDelete: () => void;
  showDelete: boolean;
  /** Custom fields (e.g. color, limits) between description and params. */
  customFields: React.ReactNode;
};

export function TemplateFormBase({
  name,
  description,
  shouldBeUsedByDefault,
  onNameChange,
  onDescriptionChange,
  onShouldBeUsedByDefaultChange,
  templateEntries,
  onTemplateEntriesAdd,
  onTemplateEntriesUpdate,
  onTemplateEntriesRemove,
  canEdit,
  saving,
  onSave,
  onDelete,
  showDelete,
  customFields,
}: TemplateFormBaseProps) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1, borderTop: darkBorder }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center" }}>
        <FormControl sx={{ flex: "1 1 160px", minWidth: 0 }}>
          <FormLabel sx={{ color: hrAccentColor }}>Nom</FormLabel>
          <DarkInput
            value={name}
            onChange={(e) => onNameChange(e.target.value)}
            disabled={!canEdit}
            slotProps={{ input: { sx: { color: textTokens.color.primary } } }}
          />
        </FormControl>
        <FormControl sx={{ flex: "1 1 160px", minWidth: 0 }}>
          <FormLabel sx={{ color: hrAccentColor }}>Utiliser par défaut</FormLabel>
          <DarkSelect
            value={shouldBeUsedByDefault ? "true" : "false"}
            onChange={(_, v) => onShouldBeUsedByDefaultChange(v === "true")}
            disabled={!canEdit}
            sx={{ minWidth: 0 }}
          >
            <Option value="false">Non</Option>
            <Option value="true">Oui</Option>
          </DarkSelect>
        </FormControl>
      </Box>
      <FormControl>
        <FormLabel sx={{ color: hrAccentColor }}>Description</FormLabel>
        <DarkInput
          value={description}
          onChange={(e) => onDescriptionChange(e.target.value)}
          disabled={!canEdit}
          slotProps={{ input: { sx: { color: textTokens.color.primary } } }}
        />
      </FormControl>
      {customFields}
      <ParamsTemplateSection
        title="Paramètres additionnels"
        entries={templateEntries}
        onAdd={onTemplateEntriesAdd}
        onUpdate={onTemplateEntriesUpdate}
        onRemove={onTemplateEntriesRemove}
        canEdit={canEdit}
        disabled={!canEdit}
        wrapInCard={false}
      />
      {canEdit && (
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <IconButtonCTA
            aria-label={saving ? "Enregistrement…" : "Enregistrer"}
            ctaVariant="accent"
            onClick={onSave}
            disabled={saving}
          >
            <SSave />
          </IconButtonCTA>
          {showDelete && (
            <IconButtonCTA aria-label="Supprimer le modèle" ctaVariant="danger" onClick={onDelete}>
              <SDelete />
            </IconButtonCTA>
          )}
        </Box>
      )}
    </Box>
  );
}
